import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, testimonials, faqs, contactInfo } from '@/mocks/aboutData';

export default function AboutPage() {

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解红桃影视品牌故事、用户口碑与联系方式。红桃影视是国内领先的高清影视资源在线观看平台，累计服务5000万+用户。"
        keywords="红桃影视关于我们,红桃影视品牌,红桃影视联系方式"
        canonicalUrl="https://hongtaomovies.com/about"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于红桃影视',
          description: aboutInfo.brandIntro,
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">
              关于红桃影视
            </h1>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-4">
                  {aboutInfo.brandIntro}
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-focus-3-line text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900 mb-1">我们的使命</h3>
                      <p className="text-sm text-foreground-500">{aboutInfo.mission}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-eye-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900 mb-1">我们的愿景</h3>
                      <p className="text-sm text-foreground-500">{aboutInfo.vision}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {aboutInfo.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 rounded-2xl p-6 text-center"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="testimonials" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm">听听用户们怎么说</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-2xl p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 text-xs font-bold">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">快速解答您的疑问</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer"
                >
                  <summary className="px-6 py-4 text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm">有任何问题欢迎联系我们</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                  {contactInfo.email}
                </a>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h3>
                <a href={`tel:${contactInfo.phone}`} className="text-sm text-foreground-600 hover:text-accent-600 cursor-pointer">
                  {contactInfo.phone}
                </a>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-500">{contactInfo.address}</p>
              </div>
            </div>

            <div className="mt-8 max-w-lg mx-auto bg-background-50 rounded-2xl p-5 text-center">
              <h4 className="text-sm font-medium text-foreground-900 mb-2">工作时间</h4>
              <p className="text-sm text-foreground-500">{contactInfo.workHours}</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}